import { Box, Divider, Flex, Image, Progress, VStack } from "@chakra-ui/react";
import { useAuth } from "@equidefi/portals/hooks/useAuth";
import { useCurrentUser } from "@equidefi/portals/hooks/useUsers";
import { Heading, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";
import { NavLink, useLocation } from "react-router";
import EquidefiLogo from "../../images/logo-white.png";
import EquidefiLogoDark from "../../images/logo.png";

const NR_OF_POSITIONS = 8;

export const WorkflowProgress = ({ offering, position }) => {
  const value = (position / NR_OF_POSITIONS) * 100; // calculates % value of investment progress

  return (
    <Flex w={{ lg: "full" }} align="center">
      <Progress variant="filled" w={`${value}%`} value={100} />
      <Progress variant="unfilled" w={`${100 - value}%`} value={0} />
    </Flex>
  );
};

export const IssuerInfo = ({ offering, isLargeScreen = false, isWideLogo = false, isLoggedIn = true }) => {
  if (!offering) return null;
  const logo = isWideLogo ? offering?.issuer?.wide_logo_url : offering?.issuer?.photo_url;
  const name = offering?.issuer?.name;

  return (
    <VStack gap="2.5" minW="full" mt={{ base: 4, lg: 0 }}>
      <Flex minW="full" alignItems={{ base: "start" }} direction={{ base: "column" }}>
        {(isLargeScreen || !isLoggedIn) && (
          <Image
            mr={{ base: 2.5, lg: 0 }}
            mb={{ base: 0, lg: 2.5 }}
            maxW={{ base: "full", lg: "max-content" }}
            w={{ base: isWideLogo ? "283px" : "100px" }}
            h={{ base: isWideLogo ? "72px" : "100px" }}
            src={logo}
            alt={name}
            borderRadius="5"
            objectFit={isWideLogo ? "contain" : "cover"}
          />
        )}
        <Box>
          {!isLargeScreen && <Text mb="0">You're investing in</Text>}

          <Heading mb="0" textStyle="h1" fontWeight="bold">
            {offering?.issuer?.name}
          </Heading>
        </Box>
      </Flex>

      <Text mb="0" ml="0" textStyle="body2" alignSelf="start">
        {offering?.name}
      </Text>
    </VStack>
  );
};

export const UserGreeting = () => {
  const user = useCurrentUser();
  if (user?.data?.entity_type === "ENTITY" && user?.data?.company) {
    return (
      <Heading fontWeight="bold" textStyle="h1" mt="4">
        {user?.data?.company}
      </Heading>
    );
  }

  return (
    <Heading fontWeight="bold" textStyle="h1" mt="4" mb="0">
      Hello{user?.data?.first_name ? `, ${user?.data?.first_name}` : "!"}
    </Heading>
  );
};

const NavItem = ({ onClick = () => {}, to, children, Icon, brandColor, ...props }) => {
  const Action = to ? NavLink : Box;
  const { pathname } = useLocation();
  const isCurrentPage = to && pathname === to;

  return (
    <Box
      as={Action}
      onClick={onClick}
      to={to}
      cursor="pointer"
      w="full"
      _hover={{
        filter: "brightness(140%)",
      }}
      _active={{
        filter: "brightness(180%)",
      }}
      background={brandColor || "primary"}
      pl={2}
      py={1}
      borderRadius="base"
      filter={isCurrentPage && "brightness(120%)"}
      {...props}>
      <Flex gap={2} my={2} opacity="90%">
        <Box as={Icon} opacity="90%" size="1.4em" />
        {children}
      </Flex>
    </Box>
  );
};

export const NavMenu = ({ logout, menu = {}, isAdditionalSignerPage, offering }) => {
  const { isLoggedIn } = useAuth();
  const location = useLocation();

  if (!isLoggedIn)
    return (
      <VStack mt={{ base: 0, lg: 4 }} alignItems="flex-start" minW="full">
        <Divider maxH={0} />
        <NavItem to="/support" Icon={Icon.HelpCircle} onClick={menu.onClose} brandColor={offering?.issuer?.brand_color}>
          Need Help?
        </NavItem>
        <NavItem
          to="/"
          state={{ redirectURL: location?.pathname }}
          Icon={Icon.LogIn}
          onClick={menu.onClose}
          brandColor={offering?.issuer?.brand_color}>
          Sign in
        </NavItem>
      </VStack>
    );

  return (
    <VStack mt={{ base: 0, lg: 4 }} alignItems="flex-start" minW="full">
      <Divider maxH={0} mb={4} />
      {!isAdditionalSignerPage && (
        <>
          <NavItem
            to="/vault/dashboard"
            Icon={Icon.Briefcase}
            onClick={menu.onClose}
            brandColor={offering?.issuer?.brand_color}>
            Portfolio
          </NavItem>
          <NavItem
            to="/vault/profile"
            Icon={Icon.User}
            onClick={menu.onClose}
            brandColor={offering?.issuer?.brand_color}>
            Account
          </NavItem>
        </>
      )}
      <NavItem to="/support" Icon={Icon.HelpCircle} onClick={menu.onClose} brandColor={offering?.issuer?.brand_color}>
        Need Help?
      </NavItem>
      <NavItem
        onClick={() => {
          logout({
            type: "success",
            message: "Logged out. Login again to access your Vault.",
          });
        }}
        Icon={Icon.LogOut}
        brandColor={offering?.issuer?.brand_color}>
        Logout
      </NavItem>
    </VStack>
  );
};

export const PoweredBySection = ({ variant = "dark" }) => (
  <Box mt={8} mb={4} textAlign="center">
    <Text
      textStyle="body2"
      fontWeight="medium"
      color={variant === "dark" ? "whiteAlpha.500" : "gray.700"}
      letterSpacing="wider"
      mb={3}>
      INVESTMENT POWERED BY
    </Text>
    <Image
      src={variant === "dark" ? EquidefiLogo : EquidefiLogoDark}
      alt="EquiDeFi"
      maxH="30px"
      maxW="120px"
      objectFit="contain"
      mx="auto"
      opacity={variant === "dark" ? "0.7" : "1"}
      filter={variant === "dark" ? "brightness(0) invert(0.6)" : "brightness(0) invert(0.1)"}
    />
  </Box>
);
